import React, { useState } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { Button, Badge, Modal, LucideIcon, message, Typography } from './ot.jsx';
import { Card, CardContent } from './card';

 
const STATUS_BADGE = {
  '進行中': { color: 'blue'  },
  '未開始': { color: 'gray'  },
  '已完成': { color: 'green' },
  '延遲':   { color: 'red'   },
};

const DEFAULT_STATS = [
  { value: 0, label: '全部任務', icon: 'ListChecks',  bgColor: 'bg-blue-50',   iconColor: 'text-blue-500'   },
  { value: 0, label: '進行中',   icon: 'Play',         bgColor: 'bg-amber-50',  iconColor: 'text-amber-500'  },
  { value: 0, label: '已完成',   icon: 'CheckCircle',  bgColor: 'bg-green-50',  iconColor: 'text-green-500'  },
  { value: 0, label: '延遲任務', icon: 'AlertCircle',  bgColor: 'bg-red-50',    iconColor: 'text-red-500'    },
];

const VIEW_MODE_OPTIONS = [
  { label: '日', value: ViewMode.Day },
  { label: '週', value: ViewMode.Week },
  { label: '月', value: ViewMode.Month },
];

const Name25 = ({
  tasks: propsTasks = null,
  stats: propsStats = null,
  propsCategoryName = '甘特圖',
}) => {
  const [viewMode, setViewMode] = useState(ViewMode.Week);
  const [detailTask, setDetailTask] = useState(null);
  const [tasks, setTasks] = useState(propsTasks || []);

  const displayStats = propsStats || DEFAULT_STATS;

  const handleTaskChange = (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? task : t));
    message.success(`已更新：${task.name}`);
  };

  const handleProgressChange = (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? task : t));
  };

  const handleSelect = (task, isSelected) => {
    if (isSelected) setDetailTask(task);
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <Typography.Title level={3} style={{ margin: 0 }}>{propsCategoryName}時程管理</Typography.Title>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {VIEW_MODE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setViewMode(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === opt.value ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button type="primary" icon={<LucideIcon name="Plus" className="w-4 h-4" />}
            onClick={() => message.info('新增任務需後端 API 配合')}>
            新增任務
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {displayStats.map((stat, idx) => (
          <Card key={idx} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className={`flex items-center gap-3 ${stat.bgColor} rounded-xl p-3`}>
                <LucideIcon name={stat.icon} className={`w-8 h-8 ${stat.iconColor}`} />
                <div>
                  <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-4">
          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <LucideIcon name="CalendarRange" className="w-16 h-16 mb-3" />
              <p className="text-sm font-medium">尚無任務資料</p>
              <p className="text-xs mt-1">請透過 tasks prop 傳入任務陣列</p>
            </div>
          ) : (
            <div className="gantt-wrapper" style={{ overflowX: 'auto' }}>
              <Gantt
                tasks={tasks}
                viewMode={viewMode}
                onDateChange={handleTaskChange}
                onProgressChange={handleProgressChange}
                onSelect={handleSelect}
                listCellWidth="200px"
                columnWidth={viewMode === ViewMode.Day ? 40 : viewMode === ViewMode.Week ? 120 : 200}
                ganttHeight={400}
                locale="zh-TW"
                todayColor="rgba(59,130,246,0.1)"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Status Legend */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-gray-600 mb-3">任務狀態說明</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(STATUS_BADGE).map(([status, cfg]) => (
              <div key={status} className="flex items-center gap-2">
                <Badge color={cfg.color}>{status}</Badge>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400 space-y-1">
            <p>• 拖曳任務列可調整起訖日期</p>
            <p>• 拖曳進度條（藍色區段）可更新完成百分比</p>
            <p>• 點擊任務列查看詳細資訊</p>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Modal
        open={!!detailTask}
        title="任務詳情"
        onCancel={() => setDetailTask(null)}
        footer={<Button onClick={() => setDetailTask(null)}>關閉</Button>}
        width={440}
      >
        {detailTask && (
          <div className="space-y-3 pt-1">
            <h3 className="text-base font-bold text-gray-800">{detailTask.name}</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {[
                { label: '類型',     value: detailTask.type === 'project' ? '專案' : detailTask.type === 'milestone' ? '里程碑' : '任務' },
                { label: '開始日',   value: detailTask.start?.toLocaleDateString?.('zh-TW') },
                { label: '結束日',   value: detailTask.end?.toLocaleDateString?.('zh-TW') },
                { label: '完成度',   value: `${Math.round(detailTask.progress ?? 0)} %` },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-400">{label}</span>
                  <span className="font-medium text-gray-700">{value}</span>
                </div>
              ))}
            </div>
            <div className="rounded-lg overflow-hidden bg-gray-200 h-2">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${detailTask.progress ?? 0}%` }} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Name25;
